import {
    useLayoutEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    Animated
} from "react-native";
import type {
    AnimatedIconTransitionHookConstantType,
    AnimatedIconTransitionHookType,
    AnimatedIconTransitionPair
} from "./type";
import {
    ANIMATED_ICON_START_PROGRESS
} from "./constant";
import {
    getAnimatedIconNodes
} from "./nodes";
import {
    getAnimatedIconTransitionPlan
} from "./util";
import {
    useReducedMotion,
    MOTION_EASINGS
} from "../../helpers/motion";

const useAnimatedIconTransition = ({
    onTransitionEnd,
    iconTransition,
    durations,
    icon
}: AnimatedIconTransitionHookConstantType): AnimatedIconTransitionHookType => {
    const isReduceMotionEnabled = useReducedMotion();

    const onTransitionEndRef = useRef(onTransitionEnd);

    const startedProgressRef = useRef<Animated.Value | null>(null);

    const [
        pair,
        setPair
    ] = useState<AnimatedIconTransitionPair>({
        from: icon,
        to: icon,
        id: 0,
        icon
    });

    if(pair.icon !== icon) {
        setPair(icon === pair.from || icon === pair.to ? {
            ...pair,
            icon
        } : {
            from: pair.icon,
            id: pair.id + 1,
            to: icon,
            icon
        });
    }

    const fromIcon = pair.from;

    const toIcon = pair.to;

    const isForward = icon === toIcon;

    const plan = useMemo(() => {
        if(!toIcon || !fromIcon || fromIcon === toIcon || iconTransition === "none") {
            return null;
        }

        const fromNodes = getAnimatedIconNodes({
            icon: fromIcon
        });

        const toNodes = getAnimatedIconNodes({
            icon: toIcon
        });

        if(!fromNodes || !toNodes) {
            return null;
        }

        return getAnimatedIconTransitionPlan({
            isReduced: isReduceMotionEnabled,
            transition: iconTransition,
            durations,
            fromNodes,
            fromIcon,
            toNodes,
            toIcon
        });
    }, [
        durations.medium,
        durations.short,
        durations.long,
        isReduceMotionEnabled,
        iconTransition,
        fromIcon,
        toIcon
    ]);

    const progress = useMemo(() => new Animated.Value(plan ? plan.start : ANIMATED_ICON_START_PROGRESS), [
        plan
    ]);

    useLayoutEffect(() => {
        onTransitionEndRef.current = onTransitionEnd;
    });

    useLayoutEffect(() => {
        if(!icon || fromIcon === toIcon) {
            return;
        }

        if(!plan) {
            setPair((current) => ({
                from: current.icon,
                icon: current.icon,
                to: current.icon,
                id: current.id
            }));

            if(iconTransition !== "none") {
                onTransitionEndRef.current?.();
            }

            return;
        }

        const target = isForward ? plan.end : plan.start;

        let animation: Animated.CompositeAnimation | undefined;

        let frameID: number | undefined;

        const handleStart = () => {
            frameID = undefined;

            startedProgressRef.current = progress;

            progress.stopAnimation((value) => {
                animation = Animated.timing(progress, {
                    duration: plan.duration * Math.abs(target - value) / Math.abs(plan.end - plan.start),
                    easing: MOTION_EASINGS.linear,
                    useNativeDriver: false,
                    isInteraction: false,
                    toValue: target
                });

                animation.start(({
                    finished
                }) => {
                    if(!finished) {
                        return;
                    }

                    setPair((current) => current.icon === icon ? {
                        id: current.id,
                        from: icon,
                        to: icon,
                        icon
                    } : current);

                    onTransitionEndRef.current?.();
                });
            });
        };

        if(isForward && startedProgressRef.current !== progress && typeof requestAnimationFrame === "function") {
            frameID = requestAnimationFrame(handleStart);
        } else {
            handleStart();
        }

        return () => {
            if(frameID !== undefined) {
                cancelAnimationFrame(frameID);
            }

            animation?.stop();
        };
    }, [
        iconTransition,
        isForward,
        fromIcon,
        progress,
        toIcon,
        plan,
        icon
    ]);

    return {
        transition: plan && fromIcon && toIcon ? {
            key: `${pair.id}:${plan.key}`,
            fromIcon,
            progress,
            toIcon,
            plan
        } : null
    };
};
export default useAnimatedIconTransition;
